<template>
  <div class='cfs-details' :style="{ 'min-height': heightCfs ? `${heightCfs}px` : 'unset' }">
    <CategoriesTreePopin ref="tree" />
    <CategoriesDropdown
      v-if='categoriesDropdown && categoriesDropdown.multiselect'
      :dropdown-style="categoriesDropdown"
      :list="displayedCategories(categories)"
      :max-level='maxCategoryDepth || 2'
      :max-select-level='maxCategoryDepth || 2'
      :preselected='selectedCategories'
      @close="categoriesDropdown = null; heightCfs = null;"
      @selected='selectedCategories = $event; categoriesDropdown = null;'
      compact-button
      disable-adding
      empty-placeholder
      multiselect
      no-selected-list
    />
    <CategoriesDropdown
      v-if='categoriesDropdown && !categoriesDropdown.multiselect'
      :dropdown-style="categoriesDropdown"
      :list="categories"
      :max-level='maxCategoryDepth || 2'
      :max-select-level='maxCategoryDepth + 1 || 2'
      @addElement="showCreateCategory"
      @close="categoriesDropdown = null; heightCfs = null; addingCategory = null;"
      @select='selectedCategory($event); categoriesDropdown = null;'
      deepest
      empty-placeholder
      forecast-enabled-only
    >
      <CreateCategoryPopup
        v-if='addingCategory'
        :displayed-categories='categories'
        :init-category='addingCategory'
        @close="addingCategory = null"
      />
    </CategoriesDropdown>
    <FlexibleDropdown
      v-if="selectedDate"
      v-click-outside='vsoConfig'
      class='cfs-datepicker'
      :position="datepickerStyle"
      :selected="`Date de ${selectedDate === 'start_date' ? 'début' : 'fin'}`"
      @close="selectedDate = ''; heightCfs = null;"
      no-padding
      no-reverse
    >
      <DatePicker
        v-model='cfsToEdit[selectedDate]'
        locale="fr"
        @input='selectedDate = ""; errors[1] = false;'
        @dayclick='$nextTick(() => recalcWrapper());'
        is-inline
      />
    </FlexibleDropdown>
    <FlexibleDropdown
      v-if='dropdownFreqShown || rollingFreq'
      :list="dropdownFreqShown ? frequencyUnits[currentFrequencyType]['values']
        : filteredFrequencyUnits"
      :position='computedStyle'
      :selected="dropdownFreqShown
        ? (frequencyUnits[currentFrequencyType]['values'][cfsToEdit[dropdownFreqShown]] ||
         frequencyUnits[currentFrequencyType]['default'])
        : frequencyUnits[currentFrequencyType]['values'][cfsToEdit[rollingFreq]]"
      @close="dropdownFreqShown=''; rollingFreq='';"
      @selected="(ev) => (dropdownFreqShown ? (e) => {
        cfsToEdit[dropdownFreqShown]=e;
        if (dropdownFreqShown === 'inv_frequency_unit') {
          autoThetaIFUUpdate(e);
        }
        dropdownFreqShown='';
        $nextTick(() => { recalcWrapper(); });
      } : (e) => {
        cfsToEdit[rollingFreq]=e;
        rollingFreq='';
        $nextTick(() => { recalcWrapper(); });
      })(ev)"
      compact
      small
    />
    <div class='cfs-header'>
      <FygrInput
        v-if='editingName && !disabled'
        class='name-edit'
        :valueIn='nameToEdit'
        @input='nameToEdit = $event; cfsToEdit.name = nameToEdit;'
        @next='updateName()'
        autofocus
        noPrompt
      />
      <div v-else class='cfs-name'>
        {{cfsToEdit.name}}
      </div>
      <div
        v-if='!disabled'
        class='edit-icon clickable'
        :class="{ editing: editingName }"
        @click='updateName()'
      >
        <i class='fi fi-edit' />
      </div>
      <div
        v-if='!disabled && !noDelete'
        class='edit-icon delete clickable'
        @click='deleteCFS()'
      >
        <i class='fi fi-delete' />
      </div>
      <div class='toggle' v-if='rolling && !disabled'>
        Glissant
        <Toggle
          :valueIn='cfsToEdit.is_rolling'
          @toggle='cfsToEdit = { ...cfsToEdit, is_rolling: $event };
            $nextTick(() => {
              recalcWrapper();
            });'
        />
      </div>
    </div>
    <div class='elements-list' :class="{ 'full-list': noScroll }">
      <div class='element-row' ref='valeur'>
        <FygrInput
          class='value'
          label='Valeur'
          info='Veuillez indiquer un montant négatif pour une dépense'
          type='fr-number'
          :disabled='disabled'
          :invalid='errors[0]'
          :placeholder="cfsToEdit.dummy_relative ? 'Ex : 25' : 'Ex : 2 500'"
          :valueIn='cfsToEdit.valeur === undefined ? "" : cfsToEdit.valeur'
          @input='cfsToEdit.valeur = $event; errors[0] = false;'
          noPrompt
          validate
        />
        <div class='ev-drop eur'>
          <div class='drop-label'>
            EUR / %
          </div>
          <div class='switch-holder eur'>
            <div
              class='switch-element'
              :class="{ disabled, 'switched-on': !cfsToEdit.dummy_relative }"
              @click="cfsToEdit = { ...cfsToEdit, dummy_relative: false,
              categories_relative: undefined }"
            >
              EUR
            </div>
            <div
              class='switch-element'
              :class="{ disabled, 'switched-on': cfsToEdit.dummy_relative }"
              @click="cfsToEdit = {
                ...cfsToEdit,
                dummy_relative: true,
                categories_relative: [],
              }"
            >
              %
            </div>
          </div>
        </div>
      </div>
      <div v-if='cfsToEdit.dummy_relative' class='element-row' ref='relcat'>
        <div class='ev-drop full'>
          <div class='drop-label absolute'>
            En fonction de la catégories / sous-catégories
          </div>
          <div
            class='drop-body absolute'
            :class="{
              invalid: errors[3],
              disabled,
            }"
            @click="showDD($event)"
          >
            {{cfsToEdit.categories_relative.length}} élément(s) sélectionnés
            <FontAwesomeIcon icon='caret-down' />
          </div>
        </div>
      </div>
      <div v-if='!cfsToEdit.is_rolling' class='element-row' ref='startd'>
        <div class='ev-drop half'>
          <div class='drop-label'>
            Date de début
          </div>
          <div
            class='drop-body'
            :class="{
              invalid: errors[1],
              disabled,
            }"
            @click.stop="openDatePicker($event, 'start_date')"
          >
            {{cfsToEdit.start_date ? formattedDate(cfsToEdit.start_date, DDMMYYYY)
              : 'Date de début'}}
            <FontAwesomeIcon icon='caret-down' />
          </div>
        </div>
        <FygrInput
          class='recurrence'
          label='Récurrence'
          placeholder='1'
          :class="{ disabled: cfsToEdit.inv_frequency_unit === 'unique' }"
          :disabled='cfsToEdit.inv_frequency_unit === "unique" || disabled'
          :valueIn='cfsToEdit.inv_frequency'
          @input='cfsToEdit.inv_frequency = $event;
            cfsToEdit.theta_inv_frequency_relative = cfsToEdit.dummy_relative
              ? $event : cfsToEdit.theta_inv_frequency_relative; recalcThetaWindow();'
          aboveZero
          noPrompt
        />
        <div class='ev-drop eur'>
          <div
            class='drop-body absolute'
            :class="{ disabled }"
            @click="displayingFreqDrop($event, 'inv_frequency_unit')"
          >
            {{frequencyUnits['regular']['values'][cfsToEdit.inv_frequency_unit]
            || frequencyUnits['regular']['default']}}
            <FontAwesomeIcon icon='caret-down' />
          </div>
        </div>
      </div>
      <div v-else class='element-row' ref='startd'>
        <FygrInput
          class='recurrence'
          label='Début (glissant) dans...'
          :disabled='disabled'
          :valueIn='cfsToEdit.rolling_start_date_inv_frequency'
          @input='cfsToEdit.rolling_start_date_inv_frequency = $event;
            $nextTick(() => {
              recalcWrapper();
            });'
          aboveZero
          noPrompt
        />
        <div class='ev-drop eur'>
          <div
            class='drop-body absolute'
            :class="{ disabled }"
            @click="displayingRollingFreq($event, 'rolling_start_date_inv_frequency_unit')"
          >
            <div>
              {{frequencyUnits['regular']['values']
              [cfsToEdit.rolling_start_date_inv_frequency_unit]
                || frequencyUnits['regular']['default']}}
            </div>
            <FontAwesomeIcon icon='caret-down' />
          </div>
        </div>
        <FygrInput
          class='recurrence'
          label='Récurrence'
          placeholder='1'
          :class="{ disabled: cfsToEdit.inv_frequency_unit === 'unique' }"
          :disabled='cfsToEdit.inv_frequency_unit === "unique" || disabled'
          :valueIn='cfsToEdit.inv_frequency'
          @input='cfsToEdit.inv_frequency = $event; recalcThetaWindow();'
          aboveZero
          noPrompt
        />
        <div class='ev-drop eur'>
          <div
            class='drop-body absolute'
            :class="{ disabled }"
            @click="displayingFreqDrop($event, 'inv_frequency_unit')"
          >
            <div>
              {{frequencyUnits['regular']['values'][cfsToEdit.inv_frequency_unit]
               || frequencyUnits['regular']['default']}}
            </div>
            <FontAwesomeIcon icon='caret-down' />
          </div>
        </div>
      </div>
      <div
        v-if='!cfsToEdit.is_rolling && cfsToEdit.inv_frequency_unit !== "unique"'
        class='element-row'
      >
        <div class='ev-drop half'>
          <div class='drop-label'>
            Date de fin (optionnel)
          </div>
          <div
            class='drop-body'
            :class="{ disabled }"
            @click.stop="openDatePicker($event, 'end_date')"
          >
            <FontAwesomeIcon
              v-if='cfsToEdit.end_date'
              icon='times'
              @mousedown.stop="cfsToEdit.end_date = null"
            />
            {{cfsToEdit.end_date ? formattedDate(cfsToEdit.end_date, DDMMYYYY) : 'Date de fin'}}
            <FontAwesomeIcon icon='caret-down' />
          </div>
        </div>
        <FontAwesomeIcon icon='arrows-alt-h' />
        <FygrInput
          class='number'
          label='Nombre de flux'
          placeholder='Ex : 0'
          :disabled='disabled'
          :valueIn='cfsToEdit.flux'
          @input='cfsToEdit.flux = $event; setEndDate($event)'
          aboveZero
          noPrompt
        />
      </div>
      <div v-else-if='cfsToEdit.inv_frequency_unit !== "unique"' class='element-row'>
        <FygrInput
          class='recurrence'
          label='Fin (glissant) dans...'
          :disabled='disabled'
          :valueIn='cfsToEdit.rolling_end_date_inv_frequency'
          @input='cfsToEdit.rolling_end_date_inv_frequency = $event;
            $nextTick(() => {
              recalcWrapper();
            });'
          aboveZero
          noPrompt
        />
        <div class='ev-drop eur'>
          <div
            class='drop-body absolute'
            :class="{ disabled }"
            @click="displayingRollingFreq($event, 'rolling_end_date_inv_frequency_unit')"
          >
            <div>
              {{frequencyUnits['regular']['values']
              [cfsToEdit.rolling_end_date_inv_frequency_unit]
                || frequencyUnits['regular']['default']}}
            </div>
            <FontAwesomeIcon icon='caret-down' />
          </div>
        </div>
        <FontAwesomeIcon class='margined-icon' icon='arrows-alt-h' />
        <FygrInput
          class='number'
          label='Nombre de flux'
          placeholder='Ex : 0'
          :valueIn='cfsToEdit.flux'
          @input='nameToEdit = $event'
          disabled
          noPrompt
        />
      </div>
      <div
        v-if='cfsToEdit.inv_frequency_unit !== "unique" && !cfsToEdit.dummy_relative'
        class='element-row no-space'
      >
        <FygrInput
          class='recurrence marg'
          label='Évolution tous les...'
          placeholder='1'
          :class="{ disabled: cfsToEdit.evolution_inv_frequency_unit === 'unique' }"
          :disabled='cfsToEdit.evolution_inv_frequency_unit === "unique" || disabled'
          :valueIn='cfsToEdit.evolution_inv_frequency'
          @input='cfsToEdit.evolution_inv_frequency = $event'
          aboveZero
          noPrompt
        />
        <div class='ev-drop eur'>
          <div
            class='drop-body absolute'
            :class="{ disabled }"
            :disabled='cfsToEdit.evolution_inv_frequency_unit === "unique" || disabled'
            :valueIn='cfsToEdit.evolution_inv_frequency_unit'
            @click="displayingFreqDrop($event, 'evolution_inv_frequency_unit')"
          >
            <div>
              {{frequencyUnits['regular']['values'][cfsToEdit.evolution_inv_frequency_unit]
               || frequencyUnits['regular']['default']}}
            </div>
            <FontAwesomeIcon icon='caret-down' />
          </div>
        </div>
      </div>
      <div
        v-if='cfsToEdit.inv_frequency_unit !== "unique"
          && cfsToEdit.evolution_inv_frequency_unit !== "unique"
          && !cfsToEdit.dummy_relative'
        class='element-row'
        ref='evolution'
      >
        <FygrInput
          class='value'
          info='Veuillez indiquer un montant négatif pour une dépense'
          label='Évolution'
          type='fr-number'
          :disabled='disabled'
          :invalid='errors[4]'
          :placeholder="cfsToEdit.dummy_exponential ? 'Ex : 25' : 'Ex : 2 500'"
          :valueIn='cfsToEdit.evolution'
          @input='cfsToEdit.evolution = $event'
          noPrompt
          validate
        />
        <div class='ev-drop eur'>
          <div class='drop-label'>
            EUR / %
          </div>
          <div class='switch-holder eur'>
            <div
              class='switch-element'
              :class="{ disabled, 'switched-on' : !cfsToEdit.dummy_exponential }"
              @click="cfsToEdit = { ...cfsToEdit, dummy_exponential: false }"
            >
              EUR
            </div>
            <div
              class='switch-element'
              :class="{ disabled,  'switched-on' : cfsToEdit.dummy_exponential }"
              @click="cfsToEdit = { ...cfsToEdit, dummy_exponential: true }"
            >
              %
            </div>
          </div>
        </div>
      </div>
      <div v-if='!category' class='element-row' ref='cats'>
        <div class='ev-drop full'>
          <div class='drop-label absolute'>
            Affecter à la catégorie
          </div>
          <div
            class='drop-body absolute'
            :class="{
              invalid: errors[2],
              disabled,
            }"
            @click="showDD($event, false)"
            @mouseenter="showPopin($event, pathfinder(categories, cfsToEdit.as_category))"
            @mouseleave="hidePopin"
          >
            {{displayedSC(categories, cfsToEdit.as_category)}}
            <FontAwesomeIcon icon='caret-down' />
          </div>
        </div>
      </div>
      <div class='advanced-options-holder text-gray'>
        <div class='clickable' @click='advancedShownCFS = !advancedShownCFS'>
          <FontAwesomeIcon
            :icon='`caret-${advancedShownCFS ? "down" : "right"}`'
          />
          Paramètres avancés
        </div>
      </div>
      <div v-if='advancedShownCFS' class='advanced-options-list text-gray' style='margin-top:15px'>
        <div class='element-row'>
          <div class='ev-drop half'>
            <FygrInput
              class='full-semi-width'
              label='Montant min'
              type='number'
              :invalid='errors[0]'
              :disabled='disabled'
              :placeholder="'Ex : -2000'"
              :valueIn='cfsToEdit.min_amount === undefined ? null : cfsToEdit.min_amount'
              @input='cfsToEdit.min_amount = $event; errors[0] = false;'
              noPrompt
              validate
            />
          </div>
          <div class='ev-drop half'>
            <FygrInput
              class='full-semi-width'
              label='Montant max'
              :disabled='disabled'
              type='number'
              :invalid='errors[0]'
              :placeholder="'Ex : 5000'"
              :valueIn='cfsToEdit.max_amount === undefined ? null : cfsToEdit.max_amount'
              @input='cfsToEdit.max_amount = $event; errors[0] = false;'
              noPrompt
              validate
            />
          </div>
        </div>
        <div v-if='cfsToEdit.dummy_relative' class='element-row left-align'>
          <FygrInput
            class='recurrence marg'
            label='Décalage / Retard (optionnel)'
            placeholder='0'
            :bubble='70'
            :bubble-width='300'
            :class="{ disabled: cfsToEdit.lag_inv_frequency_unit_relative === 'unique' }"
            :disabled='cfsToEdit.lag_inv_frequency_unit_relative === "nolag" || disabled'
            :valueIn='cfsToEdit.lag_inv_frequency_relative'
            @input='cfsToEdit.lag_inv_frequency_relative = $event; recalcThetaWindow();'
            no-prompt
          >
            <template v-slot:bubble>
              <div class='bubble-content'>
                <div class='bubble-line'>
                  Sélectionner un retard permet d'appliquer le pourcentage d'une formule relative à
                  une période antérieure d'une autre catégorie, au lieu de la période actuelle par
                  défaut.
                </div>
                <div class='bubble-line'>
                  Exemple d'utilisation : TVA mensuelle.
                </div>
                <!--<a class='text-blue clickable'>Exemple vidéo détaillé</a>-->
              </div>
            </template>
          </FygrInput>
          <div class='ev-drop eur'>
            <div
              class='drop-body absolute'
              :class="{ disabled }"
              @click="displayingFreqDrop($event, 'lag_inv_frequency_unit_relative', 'lag')"
            >
              <div>
                {{frequencyUnits['lag']['values'][cfsToEdit.lag_inv_frequency_unit_relative]
                  || frequencyUnits['lag']['default']}}
              </div>
              <FontAwesomeIcon icon='caret-down' />
            </div>
          </div>
        </div>
        <div v-if='cfsToEdit.dummy_relative' class='element-row left-align'>
          <FygrInput
            class='recurrence marg'
            label='Appliquer le % sur une durée de...'
            placeholder='1'
            :bubble='100'
            :bubble-width='300'
            :valueIn='cfsToEdit.theta_inv_frequency_relative'
            :disabled='disabled'
            @input='cfsToEdit.theta_inv_frequency_relative = $event;
              recalcThetaWindow(); changeWarning();'
            noPrompt
          >
            <template v-slot:bubble>
              <div class='bubble-content'>
                <div class='bubble-line'>
                  Par défaut, le pourcentage d'une formule relative s'applique sur une plage dont
                  la durée est égale à un mois. Sélectionner une plage temporelle permet d'appliquer
                  ce pourcentage sur une durée différente.
                </div>
                <div class='bubble-line'>
                  Exemple d'utilisation : TVA trimestrielle où il faut sélectionner un retard de
                  1 mois et une plage temporelle de 3 mois.
                </div>
                <!--<a class='text-blue clickable'>Exemple vidéo détaillé</a>-->
              </div>
            </template>
          </FygrInput>
          <div class='ev-drop eur'>
            <div
              class='drop-body absolute'
              :class="{ disabled }"
              @click="displayingFreqDrop($event, 'theta_inv_frequency_unit_relative', 'theta')"
            >
              <div>
                {{frequencyUnits['theta']['values'][cfsToEdit.theta_inv_frequency_unit_relative]
                  || frequencyUnits['theta']['default']}}
              </div>
              <FontAwesomeIcon icon='caret-down' />
            </div>
          </div>
          <div v-if='warningActive' class='red-text'>
            Pensez à vérifier que les prévisions de cette formule sont conformes à vos attentes.
          </div>
        </div>
        <div v-if='cfsToEdit.dummy_relative' class='element-row'>
          <div class='ev-drop half'>
            <FygrInput
              class='full-semi-width'
              label="Le % s'applique du ..."
              :valueIn="thetaWindowStartDate"
              disabled
            />
          </div>
          <div class='ev-drop half'>
            <FygrInput
              class='full-semi-width'
              label='au ... pour le 1er flux'
              :valueIn="thetaWindowEndDate"
              disabled
            />
          </div>
        </div>
      </div>
    </div>
    <Button
      v-if='!hideButtons'
      class='validating'
      color='green'
      :disabled='disabled && !enableButtons && cfsToEdit.is_alterable === true'
      :loading='processing'
      @click="cfsToEdit && cfsToEdit.origin && cfsToEdit.origin === 'fygr_ai'
      ? cloneFygrFormula(cfsToEdit)
      : updateData({ ...cfsToEdit })">
      {{!cfsToEdit.is_alterable && buttons[2] ? buttons[2] : buttons[0]}}
    </Button>
    <div v-if='!hideButtons' class='text-blue annuler clickable' @click="$emit('close')">
      {{buttons[1]}}
    </div>
  </div>
</template>

<script>
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import vClickOutside from 'v-click-outside';
import CategoriesDropdown from '@/components/elements/CategoriesDropdown.vue';
import FlexibleDropdown from '@/components/elements/FlexibleDropdown.vue';
import CreateCategoryPopup from '@/components/elements/popups/CreateCategoryPopup.vue';
import { categoryTreeMixin } from '@/mixins/categoryTreeMixin';
import CategoriesTreePopin from '@/components/elements/CategoriesTreePopin.vue';
import { mapActions, mapState } from 'vuex';
import { datesMixin } from '@/mixins/datesMixin';
import moment from 'moment';

export default {
  components: {
    CategoriesDropdown,
    FlexibleDropdown,
    DatePicker,
    CategoriesTreePopin,
    CreateCategoryPopup,
  },
  mixins: [categoryTreeMixin, datesMixin],
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    basePlan: {
      type: Object,
      default: () => null,
    },
    buttons: {
      type: Array,
      default: () => ['Valider', 'Annuler'],
    },
    categories: {
      type: [Array, Object],
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    enableButtons: {
      type: Boolean,
      default: false,
    },
    hideButtons: {
      type: Boolean,
      default: false,
    },
    incoming: {
      type: [Object],
      default: () => ({}),
    },
    noDelete: {
      type: Boolean,
      default: false,
    },
    noScroll: {
      type: Boolean,
      default: false,
    },
    processing: {
      type: Boolean,
      default: false,
    },
    rolling: {
      type: Boolean,
      default: false,
    },
    category: {
      type: Object,
      default: null,
    },
  },
  watch: {
    warningActive() {
      if (this.warningActive) {
        this.warnRemover = setTimeout(() => { this.warningActive = false; }, 10000);
      } else if (this.warnRemover) {
        clearTimeout(this.warnRemover);
      }
    },
    incoming() {
      this.cfsToEdit = {
        ...this.incoming,
        categories_relative: (!this.incoming.categories_relative ? []
          : this.incoming.categories_relative),
      };
      this.jsonToCategories();
    },
    selectedCategories() {
      this.cfsToEdit = { ...this.cfsToEdit, categories_relative: this.sortCategories() };
    },
    cfsToEdit(n, o) {
      if (JSON.stringify(n) !== JSON.stringify(o)) {
        this.$emit('input', this.cfsToEdit);
        this.nameToEdit = this.cfsToEdit.name;
        this.editingName = false;
        this.$nextTick(() => {
          this.recalcWrapper();
        });
      }
    },
  },
  mounted() {
    this.cfsToEdit = {
      ...this.incoming,
      categories_relative: (!this.incoming.categories_relative ? []
        : this.incoming.categories_relative),
    };
    this.jsonToCategories();
  },
  data() {
    return {
      advancedShownCFS: false,
      categoriesDropdown: null,
      currentFrequencyType: 'regular',
      cfsToEdit: {},
      dropdownFreqShown: '',
      dropdownBottom: 0,
      dropdownLeft: 0,
      dropdownTop: 0,
      editingName: false,
      errors: [],
      filteredFrequencyUnits: {
        day: 'Jour',
        week: 'Semaine',
        month: 'Mois',
        quarter: 'Trimestre',
        semestre: 'Semestre',
        year: 'An',
      },
      frequencyUnits: {
        regular: {
          values: {
            unique: 'Unique',
            day: 'Jour',
            week: 'Semaine',
            month: 'Mois',
            quarter: 'Trimestre',
            semestre: 'Semestre',
            year: 'An',
          },
          default: 'Unique',
        },
        theta: {
          values: {
            day: 'Jour',
            week: 'Semaine',
            month: 'Mois',
            quarter: 'Trimestre',
            semestre: 'Semestre',
            year: 'An',
          },
          default: 'Mois',
        },
        lag: {
          values: {
            nolag: 'Aucun',
            day: 'Jour',
            week: 'Semaine',
            month: 'Mois',
            quarter: 'Trimestre',
            semestre: 'Semestre',
            year: 'An',
          },
          default: 'Aucun',
        },
      },
      nameToEdit: '',
      rollingFreq: '',
      selectedCategories: [],
      selectedDate: '',
      selectedList: null,
      thetaWindowStartDate: 'n/a',
      thetaWindowEndDate: 'n/a',
      selectElDropdown: null,
      vsoConfig: {
        handler: () => {
          this.dropdownFreqShown = '';
          this.rollingFreq = '';
          this.selectedDate = '';
        },
        events: ['mousedown'],
      },
      warningActive: false,
      warnRemover: null,
      heightCfs: null,
    };
  },
  computed: {
    ...mapState('app', ['scalingCoeff']),
    ...mapState('user', ['maxCategoryDepth']),
    computedStyle() {
      const dict = {
        bottom: this.dropdownBottom,
        left: this.dropdownLeft,
        top: this.dropdownTop,
        height: 130,
        headerWidth: 100,
        contentWidth: 100,
      };
      return dict;
    },
    datepickerStyle() {
      const dict = {
        bottom: this.dropdownBottom,
        left: this.dropdownLeft,
        top: this.dropdownTop,
        height: 330,
        headerWidth: 150,
        contentWidth: 260,
      };
      return dict;
    },
    displayedCategories() {
      return (list) => {
        const filteredList = list.map((el) => {
          if (el.list && el.list.length) {
            const sublist = this.displayedCategories(el.list);
            if (sublist.length) {
              return {
                id: el.id,
                name: el.name,
                locked: !el.is_alterable,
                list: sublist,
                param: { selectAll: !!el.list && !!el.list.length },
              };
            }
          }
          return {
            id: el.id,
            name: el.name,
            locked: !el.is_alterable,
            list: el.list,
            param: { selectAll: !!el.list && !!el.list.length },
          };
        });
        return filteredList.filter(el => !!el);
      };
    },
    formattedDate() {
      return (date) => {
        const newDate = moment(date).isValid() ? moment(date).format('DD/MM/YYYY') : '-';
        return newDate;
      };
    },
  },
  methods: {
    ...mapActions('app', [
      'actionGetEndDate',
      'actionGetNumberOfCashFlows',
      'actionGetThetaWindow',
    ]),
    autoThetaIFUUpdate(ifu) {
      if (this.cfsToEdit.dummy_relative && (ifu !== 'unique')) {
        this.cfsToEdit.theta_inv_frequency_unit_relative = ifu;
        this.cfsToEdit.theta_inv_frequency_relative = this.cfsToEdit.inv_frequency;
      } else {
        this.cfsToEdit.theta_inv_frequency_relative = null;
        this.cfsToEdit.theta_inv_frequency_unit_relative = null;
      }
    },
    showDD(e, multiselect = true) {
      const formula = e.target.closest('.formulas-half-block');
      const target = e.target.closest('.drop-body');
      let rect = target.getBoundingClientRect();
      let diff = 0;
      if (formula) {
        const formulaRect = formula.getBoundingClientRect();
        const cfsHolderRect = e.target.closest('.cfs-details').getBoundingClientRect();
        diff = (rect.bottom + 360) - (formulaRect.bottom + 40) - 30;
        if (diff > 0 && diff + formulaRect.height > cfsHolderRect.height - 20) {
          this.heightCfs = cfsHolderRect.height + diff;
        }
        this.$nextTick(() => {
          if (diff > 0) {
            formula.scrollTo(0, formula.scrollTop + diff);
          }
        });
      }
      setTimeout(() => {
        rect = target.getBoundingClientRect();
        this.categoriesDropdown = {
          top: rect.top - 74,
          bottom: (e.view.innerHeight / this.scalingCoeff) - rect.bottom,
          right: (e.view.innerWidth / this.scalingCoeff) - rect.right,
          'min-width': rect.width + 10,
          'max-width': 340,
          height: 360,
          multiselect,
        };
      }, diff > 0 ? 300 : 0);
    },
    sortCategories() {
      return this.selectedCategories;
    },
    jsonToCategories() {
      if (!this.cfsToEdit.categories_relative) return;
      this.selectedCategories = [...this.cfsToEdit.categories_relative];
    },
    deleteCFS() {
      this.$emit('delete', this.cfsToEdit);
    },
    async updateName() {
      this.$emit('processing', true);
      if (this.editingName) {
        this.cfsToEdit.name = this.nameToEdit;
        this.$nextTick(() => {
          this.editingName = false;
          this.nameToEdit = '';
        });
      } else {
        this.editingName = true;
        this.nameToEdit = this.cfsToEdit.name;
      }
      this.$emit('processing', false);
    },
    openDatePicker(e, variable) {
      const formula = e.target.closest('.formulas-half-block');
      const target = e.target.closest('.drop-body');
      let rect = target.getBoundingClientRect();
      let diff = 0;
      if (formula) {
        const formulaRect = formula.getBoundingClientRect();
        const cfsHolderRect = e.target.closest('.cfs-details').getBoundingClientRect();
        diff = (rect.bottom + 330) - (formulaRect.bottom + 40) - 40;
        if (diff > 0 && diff + formulaRect.height > cfsHolderRect.height - 20) {
          this.heightCfs = cfsHolderRect.height + diff;
        }
        this.$nextTick(() => {
          if (diff > 0) {
            formula.scrollTo(0, formula.scrollTop + diff);
          }
        });
      }
      setTimeout(() => {
        rect = target.getBoundingClientRect();
        this.dropdownTop = rect.top;
        this.dropdownLeft = rect.left - 5;
        this.dropdownBottom = (e.view.innerHeight / this.scalingCoeff) - rect.bottom;
        this.selectedDate = variable;
      }, diff > 0 ? 300 : 0);
    },
    displayingFreqDrop(e, variable, type = 'regular') {
      const formula = e.target.closest('.formulas-half-block');
      const target = e.target.closest('.drop-body');
      let rect = target.getBoundingClientRect();
      let diff = 0;
      if (formula) {
        const formulaRect = formula.getBoundingClientRect();
        const cfsHolderRect = e.target.closest('.cfs-details').getBoundingClientRect();
        diff = (rect.bottom + 330) - (formulaRect.bottom + 40) - 40;
        if (diff > 0 && diff + formulaRect.height > cfsHolderRect.height - 20) {
          this.heightCfs = cfsHolderRect.height + diff;
        }
        this.$nextTick(() => {
          if (diff > 0) {
            formula.scrollTo(0, formula.scrollTop + diff);
          }
        });
      }
      setTimeout(() => {
        rect = target.getBoundingClientRect();
        this.currentFrequencyType = type;
        this.dropdownTop = rect.top;
        this.dropdownLeft = rect.left - 5;
        this.dropdownBottom = (e.view.innerHeight / this.scalingCoeff) - rect.bottom;
        this.$nextTick(() => { this.dropdownFreqShown = variable; });
      }, diff > 0 ? 300 : 0);
    },
    displayingRollingFreq(e, variable) {
      const target = e.target.closest('.drop-body');
      const rect = target.getBoundingClientRect();
      this.dropdownTop = rect.top;
      this.dropdownLeft = rect.left - 5;
      this.dropdownBottom = (e.view.innerHeight / this.scalingCoeff) - rect.bottom;
      this.$nextTick(() => { this.rollingFreq = variable; });
    },
    selectedCategory(path) {
      this.cfsToEdit.as_category = path[path.length - 1];
      this.cfsToEdit = { ...this.cfsToEdit };
    },
    async setEndDate(flows) {
      if (!this.cfsToEdit.is_rolling && flows) {
        this.cfsToEdit.end_date = new Date((await this.actionGetEndDate({
          start_date: this.dateToISO(this.getDate(this.cfsToEdit.start_date)),
          number_cash_flows: Number(flows),
          inv_frequency: Number(this.cfsToEdit.inv_frequency) || 1,
          inv_frequency_unit: this.cfsToEdit.inv_frequency_unit,
        })));
      }
      if (!flows) {
        this.cfsToEdit.end_date = null;
      }
      this.cfsToEdit = { ...this.cfsToEdit };
    },
    async recalcFlowsNumber() {
      if (this.cfsToEdit.inv_frequency_unit === 'unique') {
        return;
      }
      const params = {
        inv_frequency: this.cfsToEdit.inv_frequency,
        inv_frequency_unit: this.cfsToEdit.inv_frequency_unit,
      };
      if (!this.cfsToEdit.is_rolling && this.cfsToEdit.start_date
          && this.cfsToEdit.end_date) {
        params.start_date = this.dateToISO(this.getDate(this.cfsToEdit.start_date));
        params.end_date = this.dateToISO(this.getDate(this.cfsToEdit.end_date));
      } else if (this.cfsToEdit.is_rolling && this.cfsToEdit.rolling_start_date_inv_frequency
          && this.cfsToEdit.rolling_end_date_inv_frequency) {
        params.rolling_start_date_inv_frequency = this.cfsToEdit.rolling_start_date_inv_frequency;
        params.rolling_start_date_inv_frequency_unit = this.cfsToEdit
          .rolling_start_date_inv_frequency_unit;
        params.rolling_end_date_inv_frequency = this.cfsToEdit.rolling_end_date_inv_frequency;
        params.rolling_end_date_inv_frequency_unit = this.cfsToEdit
          .rolling_end_date_inv_frequency_unit;
      } else {
        this.cfsToEdit.flux = '';
        return;
      }
      this.cfsToEdit.flux = (await this.actionGetNumberOfCashFlows(params)).number_of_cash_flows;
      this.cfsToEdit = { ...this.cfsToEdit };
    },
    async onFrequencySelect(fu) {
      /* eslint-disable */
      this.cfsToEdit[this.dropdownFreqShown] = fu[0];
      /* eslint-enable */
      this.cfsToEdit.theta_inv_frequency_unit_relative = this.cfsToEdit.dummy_relative
        && this.dropdownFreqShown === 'inv_frequency_unit'
        && this.cfsToEdit.inv_frequency_unit !== 'unique' ? this.cfsToEdit.inv_frequency_unit
        : this.cfsToEdit.theta_inv_frequency_unit_relative;
      if (this.dropdownFreqShown === 'theta_inv_frequency_unit_relative') {
        this.changeWarning();
      }
      this.dropdownFreqShown = '';
      this.$nextTick(() => {
        this.recalcWrapper();
      });
    },
    changeWarning() {
      this.warningActive = this.cfsToEdit.theta_inv_frequency_unit_relative
        !== this.cfsToEdit.inv_frequency_unit
          || this.cfsToEdit.theta_inv_frequency_relative !== this.cfsToEdit.inv_frequency;
    },
    async recalcThetaWindow() {
      if (this.cfsToEdit.inv_frequency_unit === 'unique') {
        return;
      }
      if (this.cfsToEdit.start_date
          && (this.cfsToEdit.theta_inv_frequency_unit_relative !== 'unique')
          && this.cfsToEdit.theta_inv_frequency_relative) {
        const params = {
          start_date: this.formattedDate(this.cfsToEdit.start_date, this.DDMMYYYY_DASH),
          theta_inv_frequency_relative: Number(this.cfsToEdit.theta_inv_frequency_relative),
          theta_inv_frequency_unit_relative: this.cfsToEdit.theta_inv_frequency_unit_relative,
        };
        if (this.cfsToEdit.lag_inv_frequency_relative
            && this.cfsToEdit.theta_inv_frequency_unit_relative) {
          params.lag_inv_frequency_relative = Number(
            this.cfsToEdit.lag_inv_frequency_relative,
          );
          params.lag_inv_frequency_unit_relative = this.cfsToEdit.lag_inv_frequency_unit_relative;
        }
        const res = (await this.actionGetThetaWindow(params));
        if (!res.error) {
          if (res.result && res.result.theta_start_date && res.result.theta_end_date) {
            this.thetaWindowStartDate = this.formattedDate(res.result.theta_start_date,
              this.DDMMYYYY);
            this.thetaWindowEndDate = this.formattedDate(res.result.theta_end_date, this.DDMMYYYY);
          }
        } else {
          this.thetaWindowStartDate = 'n/a';
          this.thetaWindowEndDate = 'n/a';
        }
      }
    },
    async recalcWrapper() {
      this.recalcFlowsNumber();
      this.recalcThetaWindow();
    },
    validateCFSToEdit(cfsToEdit) {
      this.errors = [
        !cfsToEdit.valeur,
        cfsToEdit.is_rolling ? !cfsToEdit.rolling_start_date_inv_frequency : !cfsToEdit.start_date,
        cfsToEdit.dummy_relative && (!cfsToEdit.categories_relative
          || !cfsToEdit.categories_relative.length),
        cfsToEdit.inv_frequency_unit !== 'unique'
          && cfsToEdit.evolution_inv_frequency_unit !== 'unique'
          && !cfsToEdit.dummy_relative && !cfsToEdit.evolution,
      ];
      if (cfsToEdit.dummy_relative) {
        this.errors.push(!cfsToEdit.categories_relative.length);
      }
      if (this.errors.includes(true)) {
        if (this.errors[0]) {
          this.$refs.valeur.scrollIntoView(true);
        } else if (this.errors[1]) {
          this.$refs.startd.scrollIntoView(true);
        } else if (this.errors[2]) {
          this.$refs.cats.scrollIntoView(true);
        } else if (this.errors[3]) {
          this.$refs.relcat.scrollIntoView(true);
        } else if (this.errors[4]) {
          this.$refs.evolution.scrollIntoView(true);
        }
        return false;
      }
      return true;
    },
    cloneFygrFormula(prepared) {
      this.$emit('clone', prepared);
    },
    updateData(prepared) {
      const cfsToEdit = prepared ? { ...prepared } : { ...this.cfsToEdit };
      if (!cfsToEdit.as_category) {
        cfsToEdit.as_category = this.category.id || this.category;
      }
      if (!this.validateCFSToEdit(cfsToEdit)) {
        return;
      }
      this.$emit('update', cfsToEdit);
    },
  },
};
</script>

<style lang="scss">
  .vc-day[class] {
    min-height: 16px;
  }
  .vc-title[class], .vc-w-12y[class] {
    text-transform: uppercase;
    font-size: 14px;
  }
  .vc-weeks[class] {
    min-width: 200px
  }
</style>
<style lang="scss" scoped>
  .red-text {
    color: $red;
    font-size: 12px;
    margin-top: 20px;
    margin-left: 10px;
    width: 160px;
  }
  .s-dropdown {
    display: flex;
    width: 277px;
    height: 40px;
    padding: 0 15px;
    position: relative;
    align-items: center;
    background-color: $bg;
    color: $text-color-gray;
    border-radius: 10px;
    &.invalid {
      box-shadow: 0.01px 0.01px 0.01px 1.3px $red;
    }
    svg {
      margin-left: auto;
    }
  }
  .bubble-content {
    width: 300px;
    .bubble-line {
      margin-bottom: 20px;
    }
  }
  .switch-element.disabled {
    cursor: default;
    pointer-events: none;
    user-select: none;
    opacity: 0.6;
  }
  ::v-deep.vc-border {
    border: none;
  }
  ::v-deep.vc-day-content {
    width: 45px !important;
    height: 35px !important;
  }
  ::v-deep.vc-rounded-full {
    border-radius: 0;
  }
  ::v-deep.vc-font-medium {
    font-weight: initial;
  }
  ::v-deep.vc-text-gray-500 {
    color: $gray;
    font-weight: initial;
  }
  ::v-deep.vc-text-gray-600 {
    color: $text-color-gray;
    font-weight: initial;
    &:hover {
      background-color: $hover;
      color: $text-color-gray;
      opacity: 1;
    }
  }
  ::v-deep.vc-container {
    --day-content-bg-color-hover: none;
    --day-content-bg-color-focus: none;
    --highlight-height: calc(100% - 3.2px);
    border: none;
  }
  ::v-deep.vc-bg-blue-600 {
    background-color: $blue;
  }
  ::v-deep.vc-highlight {
    margin: 1.6px 0;
  }
  ::v-deep.vc-bg-gray-800 {
    background-color: $ebony;
  }
  ::v-deep.vc-day-content[class] {
    &:focus:not(.vc-font-bold) {
      color: $text-color;
      font-weight: initial;
    }
  }
  ::v-deep.vc-day-content {
    &:hover:not(.vc-font-bold) {
      background-color: $hover;
      color: $text-color;
    }
  }
  .cfs-datepicker ::v-deep .slot {
    display: flex;
    justify-content: center;
  }
  .cfs-datepicker ::v-deep .content {
    padding: 0;
  }
  .cfs-details {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 350px;
    height: 434px;
    .cfs-header {
      display: flex;
      color: $text-color-gray;
      align-items: center;
      margin-top: 5px;
      margin-bottom: 15px;
      min-height: 30px;
      width: 100%;
      .name-edit {
        margin-right: 10px;
        width: 140px;
        height: 30px;
        ::v-deep.prefixed-input ::v-deepinput {
          min-height: 28px;
          max-height: 28px;
        }
      }
      .cfs-name {
        margin-right: 10px;
        min-width: 110px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding: 0 15px;
      }
      .toggle {
        display: flex;
        margin-right: 37px;
        margin-left: auto;
        .toggle-holder {
          margin-left: 10px;
        }
      }
      .edit-icon {
        width: 16px;
        height: 16px;
        margin-right: 7px;
        color: $gray;
        &.editing {
          color: darken($gray, 20);
        }
        &:hover {
          color: darken($gray, 10);
        }
        &.awesome {
          padding-bottom: 3px;
        }
        &.delete {
          &:hover {
            color: $red;
          }
          &.no-auto {
            margin-right: 15px;
          }
        }
      }
    }
    .validating, .annuler {
      min-width: 150px;
      max-width: 200px;
      margin-left: -17px;
      margin-top: 15px;
      text-align: center;
    }
    .validating {
      height: 40px;
      min-height: 40px;
    }
    .s-dropdown {
      display: flex;
      width: 277px;
      height: 40px;
      padding: 0 15px;
      position: relative;
      align-items: center;
      background-color: $bg;
      color: $text-color-gray;
      border-radius: 10px;
      &.invalid {
        box-shadow: 0.01px 0.01px 0.01px 1.3px $red;
      }
      svg {
        margin-left: auto;
      }
    }
    .bubble-content {
      width: 300px;
      .bubble-line {
        margin-bottom: 20px;
      }
    }
    .elements-list {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-height: 290px;
      max-height: 340px;
      overflow: auto;
      padding-bottom: 3px;
      &.full-list {
        min-height: unset;
        max-height: unset;
        overflow: unset;
      }
      .element-row {
        display: flex;
        justify-content: space-between;
          padding-left: 2px;
        padding-right: 37px;
        margin-bottom: 15px;
        &.left-align {
          justify-content: start ;
        }
        &:last-child {
          margin-bottom: 0;
        }
        &.no-space {
          justify-content: flex-start;
        }
        .value {
          width: 195px;
        }
        .number {
          width: 100px;
        }
        .full-semi-width {
          width: 148px;
        }
        .margined-icon {
          margin-left: 10px;
          margin-right: 10px;
        }
        .recurrence {
          width: 40px;
          position: relative;
          &.disabled ::v-deepinput {
            opacity: 0.7;
          }
          &.marg {
            margin-right: 10px;
          }
          ::v-deep.label {
            position: absolute;
            white-space: nowrap;
          }
          ::v-deep.prefixed-input {
            margin-top: 19px;
            ::v-deepinput {
              padding: 0 5px;
              width: 30px;
            }
          }
        }
        >svg {
          margin-top: 29px;
          color: $text-color-gray;
          font-size: 20px;
        }
        .switch-holder {
          height: 32px;
          width: 92px;
        }
        .ev-drop {
          display: flex;
          flex-direction: column;
          position: relative;
          ::first-letter{
            text-transform: capitalize;
          }
          &.full {
            width: 100%;
          }
          &.eur {
            width: 100px;
          }
          &.half {
            width: 150px;
          }
          .drop-label {
            color: $text-color-gray;
            white-space: nowrap;
            &.absolute {
              position: absolute;
            }
          }
          .drop-body {
            display: flex;
            align-items: center;
            justify-content: space-between;
            cursor: pointer;
            user-select: none;
            padding: 0 15px;
            background-color: $bg;
            height: 41px;
            border-radius: 10px;
            color: $text-color-gray;
            &.invalid {
              box-shadow: 0.01px 0.01px 0.01px 1.3px $red;
            }
            &.disabled {
              pointer-events: none;
              cursor: default;
              opacity: 0.6;
            }
            &.absolute {
              margin-top: 19px;
            }
            svg {
              color: $gray;
            }
          }
        }
      }
    }
  }
  .dropdown {
    z-index: 100;
    position: fixed;
    animation-name: example;
    animation-duration: 4s;
    display: flex;
    flex-direction: column;
    .head {
      z-index: 102;
      width: 140px;
      height: 20px;
      background-color: white;
      position: relative;
      display: flex;
      padding: 10px 15px;
      align-items: center;
      justify-content: space-between;
      border-radius: 10px 10px 0 0;
      -webkit-box-shadow: 0 -6px 18px -10px rgba(0,0,0,0.25);
      -moz-box-shadow: 0 -6px 18px -10px rgba(0,0,0,0.25);
      box-shadow: 0 -6px 18px -10px rgba(0,0,0,0.25);
      &.cat {
        width: 120px;
        svg {
          color: $gray;
        }
      }
      i {
        transform: scaleY(-1);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 30px;
        color: $gray;
      }
      .dd-title i {
        font-size: 16px;
        transform: none;
      }
    }
    .head:after {
      content: '';
      width: 40px;
      height: 30px;
      border: 4px solid white;
      position: absolute;
      bottom: -4px;
      border-top: 0;
      border-right: 0;
      border-radius: 0 0 0 12px;
      right:-40px;
    }
    .content {
      z-index: 99;
      width: 350px;
      padding-bottom: 15px;
      background-color: white;
      padding-top: 20px;
      border-radius: 0 10px 10px 10px;
      -webkit-box-shadow: 2px 2px 20px -5px rgba(0,0,0,0.25);
      -moz-box-shadow: 2px 2px 20px -5px rgba(0,0,0,0.25);
      box-shadow: 2px 2px 20px -5px rgba(0,0,0,0.25);
      transform-origin: top;
      animation: expandY .25s;
      &.small {
        width: 230px;
        padding-bottom: 10px;
        padding-top: 10px;
      }
      &.date {
        width: 330px;
        padding: 5px 5px;
      }
      &.cat {
        width: 190px;
        padding: 5px 0;
        .items {
          max-height: 210px;
          overflow: auto;
          .item {
            padding: 5px 15px;
            &:hover {
              background-color: $hover;
            }
          }
        }
      }
      .items {
        max-height: 400px;
        overflow: auto;
      }
      .adder {
        display: flex;
        padding: 20px;
        justify-content: center;
        &:visited {
          color: $blue;
        }
        .clickable:hover {
          color: rgba($blue, .75);
        }
      }
    }
  }
  .dropdown-freq {
    z-index: 100;
    position: fixed;
    animation-name: example;
    animation-duration: 4s;
    display: flex;
    flex-direction: column;
    text-transform: capitalize;
    .head {
      z-index: 102;
      width: 70px;
      height: 20px;
      background-color: white;
      position: relative;
      display: flex;
      padding: 10px 15px;
      align-items: center;
      justify-content: space-between;
      border-radius: 10px 10px 0 0;
      -webkit-box-shadow: 0 -6px 18px -10px rgba(0,0,0,0.25);
      -moz-box-shadow: 0 -6px 18px -10px rgba(0,0,0,0.25);
      box-shadow: 0 -6px 18px -10px rgba(0,0,0,0.25);
      svg {
        color: $gray;
      }
      .dd-title i {
        font-size: 16px;
        transform: none;
      }
    }
    .content {
      z-index: 99;
      width: 100px;
      background-color: white;
      border-radius: 0 0 10px 10px;
      -webkit-box-shadow: 2px 2px 20px -5px rgba(0,0,0,0.25);
      -moz-box-shadow: 2px 2px 20px -5px rgba(0,0,0,0.25);
      box-shadow: 2px 2px 20px -5px rgba(0,0,0,0.25);
      transform-origin: top;
      animation: expandY .25s;
      .items {
        max-height: 210px;
        padding: 5px 0;
        overflow: auto;
        .item {
          padding: 5px 15px;
          &:hover {
            background-color: $hover;
          }
        }
      }
    }
  }
</style>
